<template>
    <div data-test-view="account">
        <l-headline
            :title="$t(`adminSettings.accountHeadlineTitle`)"
            :sub-title="$t(`adminSettings.accountHeadlineSubtitle`)"
        ></l-headline>

        <l-input-text
            ref="input-name"
            v-model="team.name"
            type="text"
            :label="''"
            :placeholder="$t(`adminSettings.accountNamePlaceholder`)"
            data-test-id="team-settings-account-name"
        />
        <l-input-text
            ref="input-vision"
            v-model="team.vision"
            type="text"
            :optional="true"
            :label="''"
            :placeholder="$t(`adminSettings.accountVisionPlaceholder`)"
            data-test-id="team-settings-account-vision"
        />

        <span class="btn-group">
            <form name="upload-logo">
                <label class="btn btn-primary btn-raised btn-file btn-block" for="upload-logo">
                    <span
                        v-b-popover.hover.right="{
                            content: $t(`adminSettings.accountUploadLogoHelp`),
                        }"
                        >{{ $t(`adminSettings.accountUploadLogoBtn`) }}</span
                    >
                    <input
                        id="upload-logo"
                        type="file"
                        name="file"
                        accept="image/*"
                        style="display: none;"
                        @change="e => uploadLogo(e)"
                        @keypress="e => uploadLogo(e)"
                    />
                </label>
            </form>
        </span>

        <span class="pull-right logo-background">
            <img
                v-if="Boolean(team.settings.logoURL)"
                :src="team.settings.logoURL"
                alt="Logo"
                height="25"
            />
            <img v-else :src="leapsomeLogo" alt="Logo" height="25" />

            <span
                v-if="team.settings.logoURL && team.settings.logoURL != '/img/leapsome.png'"
                v-b-popover.hover.left="{ content: $t(`adminSettings.accountRestoreLogoBtn`) }"
                class="l-default-margin clickable"
                @click="() => resetLogo()"
                @keyup.enter="() => resetLogo()"
            >
                <i class="far fa-trash color-white"></i>
            </span>
        </span>

        <hr />

        <label class="color-dark" for="settings-useMeridianTime-check">
            <input
                id="settings-useMeridianTime-check"
                v-model="team.settings.useMeridianTime"
                type="checkbox"
                data-test-id="team-settings-account-meridian-time"
            />{{ $t(`adminSettings.accountUseMeridianLabel`) }}
        </label>
        <br />
        <label class="color-dark" for="settings-orgChartEnabled-check">
            <input
                id="settings-orgChartEnabled-check"
                v-model="team.settings.orgChartEnabled"
                type="checkbox"
                data-test-id="team-settings-account-org-chart"
            />{{ $t(`adminSettings.accountOrgChartLabel`) }}
        </label>
        <br />
        <label class="color-dark" for="settings-skillMatrixEnabled-check">
            <input
                id="settings-skillMatrixEnabled-check"
                v-model="team.settings.skillMatrixEnabled"
                type="checkbox"
                data-test-id="team-settings-account-skill-matrix"
            />{{ $t(`adminSettings.accountSkillMatrixLabel`) }}
        </label>
        <!--
        <br />
        <label class="color-dark">
            <input
                v-model="team.settings.impersonationAccessEnabled"
                type="checkbox"
                data-test-id="team-settings-account-impersonation-access"
            />{{ $t(`adminSettings.accountImpersonationAccessLabel`) }}
        </label>
        -->

        <div v-show="updateSuccess">
            <br />
            <l-text-alert
                type="success"
                :text="$t(`adminSettings.accountUpdateSuccess`)"
                data-test-id="team-settings-account-success"
            />
        </div>
        <div v-show="updateError">
            <br />
            <l-text-alert
                type="warning"
                :text="$t(`adminSettings.accountUpdateError`)"
                data-test-id="team-settings-account-error"
            />
        </div>

        <div class="row">
            <div class="col-md-offset-8 col-md-4">
                <button
                    class="btn btn-primary btn-raised btn-block"
                    data-test-id="team-settings-account-update"
                    @click="updateGlobalTeam()"
                >
                    {{ $t(`adminSettings.accountUpdateBtn`) }}
                </button>
            </div>
        </div>

        <hr />

        <l-headline :title="$t(`adminSettings.accountDummyDataHeadlineTitle`)"></l-headline>

        <p>
            <l-text :text="$t(`adminSettings.accountDummyDataText`)" />
        </p>
        <l-text-alert
            v-show="removeDummySuccess"
            type="success"
            :text="$t(`adminSettings.accountDummyDataSuccess`)"
            data-test-id="team-settings-account-remove-dummy-data-success"
        />

        <l-text-alert
            v-show="removeDummyError"
            type="warning"
            :text="$t(`adminSettings.accountDummyDataError`)"
            data-test-id="team-settings-account-remove-dummy-data-error"
        />

        <div class="row">
            <div class="col-md-offset-8 col-md-4">
                <button
                    class="btn btn-primary btn-raised btn-block"
                    data-test-id="team-settings-account-remove-dummy-data"
                    @click="removeDummyData()"
                >
                    {{ $t(`adminSettings.accountRemoveDummyDataBtn`) }}
                </button>
            </div>
        </div>
    </div>
</template>

<script>
import { mapGetters } from "vuex";
import api from "@/api";
import leapsomeLogo from "@/img/leapsome.png";

import LHeadline from "@/components/LHeadline";
import LInputText from "@/components/LInputText";
import LText from "@/components/LText";
import LTextAlert from "@/components/LTextAlert";

export default {
    components: {
        LHeadline,
        LInputText,
        LText,
        LTextAlert,
    },
    data() {
        return {
            team: {},
            removeDummySuccess: false,
            removeDummyError: false,
            updateSuccess: false,
            updateError: false,
        };
    },
    computed: {
        ...mapGetters({
            rights: "users/current/rights",
            getTeamInfo: "teams/current/info",
        }),
    },
    created() {
        // Cloning the information from the store so we can modify it before updating the store,
        // when the user clicks on the update button.
        //
        this.team = this.getTeamInfo();
        this.leapsomeLogo = leapsomeLogo;
    },
    methods: {
        isValid() {
            const inputs = Object.keys(this.$refs)
                .map(key => (key.startsWith("input-") ? this.$refs[key] : null))
                .filter(Boolean);

            return inputs.reduce((a, c) => c.validate() && a, true);
        },
        updateGlobalTeam() {
            this.updateSuccess = false;
            this.updateError = false;

            if (!this.isValid()) {
                return;
            }

            this.$store
                .dispatch({
                    type: "teams/current/update",
                    payload: {
                        scope: "name",
                        update: {
                            name: this.team.name,
                            vision: this.team.vision,
                            logoURL: this.team.settings.logoURL,
                            useMeridianTime: this.team.settings.useMeridianTime,
                            orgChartEnabled: this.team.settings.orgChartEnabled,
                            skillMatrixEnabled: this.team.settings.skillMatrixEnabled,
                            impersonationAccessEnabled: this.team.settings
                                .impersonationAccessEnabled,
                        },
                    },
                })
                .then(() => {
                    this.updateSuccess = true;
                })
                .catch(() => {
                    this.updateError = true;
                });
        },
        removeDummyData() {
            this.removeDummySuccess = false;
            this.removeDummyError = false;

            this.$store
                .dispatch({
                    type: "teams/current/removeDummyData",
                })
                .then(() => {
                    this.removeDummySuccess = true;
                })
                .catch(() => {
                    this.removeDummyError = true;
                });
        },
        uploadLogo(e) {
            this.error = false;

            const { files } = e.target;

            if (!files) {
                return;
            }

            const file = files[0];

            const formData = new FormData();
            formData.append("file", file);

            api.post("/api/uploads/logo", formData)
                .then(res => {
                    this.team.settings.logoURL = res?.data?.picture;
                    this.$forceUpdate();
                })
                .catch(() => {
                    this.error = true;
                });
        },
        resetLogo() {
            this.team.settings.logoURL = "/img/leapsome.png";
            this.$forceUpdate();
        },
    },
};
</script>

<style scoped>
.logo-background {
    background-color: #211941;
    border-radius: 7px;
    padding: 15px;
}
</style>
